import React, { useId, useRef, useState } from 'react';
import { Share2 } from 'lucide-react';
import type { IArticle } from '../../services/articleService';

type ShareArticle = Pick<IArticle, 'id' | 'slug' | 'title'>;

export default function ArticleShare({ article }: { article: ShareArticle }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const url = new URL(`/article/${encodeURIComponent(article.slug || article.id)}`, window.location.origin).href;
  const supportsNativeShare = typeof navigator.share === 'function';

  const copyLink = async () => {
    setMessage('');
    setBusy(true);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setMessage('Link copied.');
    } catch {
      linkRef.current?.focus();
      linkRef.current?.select();
      setMessage('Select and copy the link above.');
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setMessage('');
    if (!supportsNativeShare) {
      setOpen(!open);
      return;
    }
    setOpen(false);
    setBusy(true);
    try {
      await navigator.share({ title: article.title, url });
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        setOpen(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return <footer className="article-share" onKeyDown={event => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }}>
    <button ref={triggerRef} type="button" className="article-share-trigger" disabled={busy} aria-expanded={open || !supportsNativeShare ? open : undefined} aria-controls={open ? panelId : undefined} onClick={share}>
      <Share2 size={16} aria-hidden="true" /> Share
    </button>
    {open && <div id={panelId} className="article-share-panel" role="region" aria-label="Share this article">
      <div className="article-share-link">
        <input ref={linkRef} type="text" aria-label="Article link" value={url} readOnly onFocus={event => event.target.select()} />
        <button type="button" disabled={busy} onClick={copyLink}>Copy link</button>
      </div>
    </div>}
    <p className="article-share-status" role="status">{message}</p>
  </footer>;
}
